import { Modal } from "@msys/ui";
import { useTranslate } from "@tolgee/react";
import { uniqueId } from "lodash-es";
import React from "react";
import { FormikObserveState } from "../../../commons/FormikStateObserver.js";
import { BuildingCreateForm } from "../BuildingCreateForm.js";

interface Props extends Omit<
  React.ComponentProps<typeof BuildingCreateForm>,
  "handleFormikObserveStateChange" | "formId" | "handleComplete"
> {
  id?: string;
  title?: string;
  handleClose: () => void;
  handleComplete?: (
    building: Parameters<
      Exclude<
        React.ComponentProps<typeof BuildingCreateForm>["handleComplete"],
        undefined
      >
    >[0],
    buildingRootItemId: string,
    handleClose: () => void
  ) => Promise<void> | void;
}

export const BuildingCreateModal = ({
  id,
  title,
  handleClose,
  ...props
}: Props) => {
  const { t } = useTranslate(["BuildingCreate", "Global"]);

  const formId = React.useMemo(() => uniqueId(), []);
  const [formikState, setFormikState] =
    React.useState<FormikObserveState | null>(null);

  return (
    <Modal
      id={id}
      title={
        title ??
        t("Create building", {
          ns: "BuildingCreate",
        })
      }
      handleClose={handleClose}
      actionButtons={[
        {
          label: t("Cancel", {
            ns: "Global",
          }),
          handleClick: handleClose,
          buttonProps: { variant: "text" },
        },
        {
          label: t("Create building", {
            ns: "BuildingCreate",
          }),
          buttonProps: {
            form: formId,
            type: "submit",
            disabled: !formikState?.dirty || !formikState?.isValid,
            loading: formikState?.isSubmitting,
          },
        },
      ]}
    >
      <BuildingCreateForm
        {...props}
        formId={formId}
        handleFormikObserveStateChange={setFormikState}
        handleComplete={async (building, buildingRootItemId) => {
          await props.handleComplete?.(
            building,
            buildingRootItemId,
            handleClose
          );
        }}
      />
    </Modal>
  );
};
